// src/components/Sidebar.js
import React, { useState, useContext } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Send as SendIcon,
  TextSnippet as TextSnippetIcon,
  Image as ImageIcon,
  Chat as ChatIcon,
  Login as LoginIcon,
  AppRegistration as AppRegistrationIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { NavLink, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import BarChartIcon from "@mui/icons-material/BarChart";

const drawerWidth = 240;

export default function Sidebar({ children }) {
  const [sendOpen, setSendOpen] = useState(false);
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSendClick = () => setSendOpen(!sendOpen);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div style={{ display: "flex" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            bgcolor: "#111827",
            color: "#fff",
            boxSizing: "border-box",
            borderRight: "none",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "center", py: 2 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            noWrap
            sx={{ fontSize: "1.1rem", color: "#E5E7EB" }}
          >
            WhatsApp Tool
          </Typography>
        </Toolbar>
        <Divider sx={{ bgcolor: "#374151" }} />

        <List sx={{ pt: 1 }}>
          {/* Dashboard */}
          <SidebarItem to="/" icon={<DashboardIcon />} label="Dashboard" />

          {/* Send Message with Nested Items */}
          <ListItemButton onClick={handleSendClick} sx={listItemStyle}>
            <ListItemIcon sx={iconStyle}>
              <SendIcon />
            </ListItemIcon>
            <ListItemText
              primary="Send Message"
              primaryTypographyProps={textStyle}
            />
            {sendOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={sendOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <SidebarItem
                to="/send/create-template"
                icon={<ImageIcon />}
                label="Create Template"
                nested
              />
              <SidebarItem
                to="/send/text"
                icon={<TextSnippetIcon />}
                label="Message Template"
                nested
              />
              {/* Uncomment if media support is added
              <SidebarItem
                to="/send/media"
                icon={<ImageIcon />}
                label="Media Messages"
                nested
              />
              */}
            </List>
          </Collapse>

          {/* Templates */}
          <SidebarItem
            to="/templates"
            icon={<TextSnippetIcon />}
            label="Templates"
          />

          {/* Chat */}
          <SidebarItem to="/chat" icon={<ChatIcon />} label="Chatbox" />

          {/* Reports */}
          <SidebarItem to="/reports" icon={<BarChartIcon />} label="Reports" />

          {/* Login/Signup or Logout */}
          {!user ? (
            <>
              <Divider sx={{ my: 1, bgcolor: "#374151" }} />
              <SidebarItem to="/login" icon={<LoginIcon />} label="Login" />
              <SidebarItem
                to="/signup"
                icon={<AppRegistrationIcon />}
                label="Signup"
              />
            </>
          ) : (
            <>
              {/* <Divider sx={{ my: 1, bgcolor: "#374151" }} />
              <ListItemButton onClick={handleLogout} sx={listItemStyle}>
                <ListItemIcon sx={iconStyle}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={textStyle}
                />
              </ListItemButton> */}
            </>
          )}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        {children}
      </Box>
    </div>
  );
}

// ---------------------------
// Reusable Sidebar Item Component
// ---------------------------
const SidebarItem = ({ to, icon, label, nested }) => (
  <ListItemButton
    component={NavLink}
    to={to}
    sx={{
      pl: nested ? 4 : 2,
      "&.active": {
        bgcolor: "#1F2937",
        borderRadius: 2,
      },
      ...listItemStyle,
    }}
  >
    <ListItemIcon sx={iconStyle}>{icon}</ListItemIcon>
    <ListItemText primary={label} primaryTypographyProps={textStyle} />
  </ListItemButton>
);

// ---------------------------
// Styling Constants
// ---------------------------
const listItemStyle = {
  borderRadius: 2,
  px: 2,
  py: 1,
  mx: 1,
  my: 0.5,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    bgcolor: "#1F2937",
  },
};

const iconStyle = {
  color: "#9CA3AF",
  minWidth: "32px",
};

const textStyle = {
  fontSize: "0.875rem",
  color: "#E5E7EB",
};
